/**
 * Loads X (Twitter) widgets.js once per page for Embedded Tweets.
 * @see https://developer.x.com/en/docs/x-for-websites/javascript-api/guides/scripting-loading-and-initialization
 */

const TWITTER_WIDGETS_SRC = 'https://platform.twitter.com/widgets.js';

export type TwitterWidgetsApi = {
    widgets: {
        createTweet: (
            tweetId: string,
            element: HTMLElement,
            options?: Record<string, unknown>,
        ) => Promise<HTMLElement | undefined>;
    };
};

declare global {
    interface Window {
        twttr?: {
            ready: (callback: (twttr: TwitterWidgetsApi) => void) => void;
            widgets?: TwitterWidgetsApi['widgets'];
        };
    }
}

let loadPromise: Promise<TwitterWidgetsApi> | null = null;

function resolveReadyApi(): Promise<TwitterWidgetsApi> {
    return new Promise((resolve, reject) => {
        const twttr = window.twttr;
        if (!twttr?.ready) {
            reject(new Error('Twitter widgets.js loaded without twttr.ready'));
            return;
        }
        twttr.ready((api) => {
            if (!api?.widgets?.createTweet) {
                reject(new Error('Twitter widgets API unavailable'));
                return;
            }
            resolve(api);
        });
    });
}

/** Idempotent: returns the same promise while loading; resolves to widgets API. */
export function loadTwitterWidgets(): Promise<TwitterWidgetsApi> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Twitter widgets require a browser'));
    }

    if (window.twttr?.widgets?.createTweet) {
        return Promise.resolve({ widgets: window.twttr.widgets });
    }

    if (loadPromise) {
        return loadPromise;
    }

    loadPromise = new Promise<TwitterWidgetsApi>((resolve, reject) => {
        const finish = () => {
            resolveReadyApi().then(resolve).catch(reject);
        };

        const existing = document.querySelector<HTMLScriptElement>(
            `script[src="${TWITTER_WIDGETS_SRC}"]`,
        );
        if (existing) {
            if (window.twttr?.ready) {
                finish();
            }
            else {
                existing.addEventListener('load', finish, { once: true });
                existing.addEventListener(
                    'error',
                    () => reject(new Error('Failed to load Twitter widgets.js')),
                    { once: true },
                );
            }
            return;
        }

        const script = document.createElement('script');
        script.src = TWITTER_WIDGETS_SRC;
        script.async = true;
        script.onload = finish;
        script.onerror = () => {
            loadPromise = null;
            reject(new Error('Failed to load Twitter widgets.js'));
        };
        document.body.appendChild(script);
    });

    return loadPromise;
}

/** Test helper — clears the singleton load promise. */
export function resetTwitterWidgetsLoaderForTests(): void {
    loadPromise = null;
}
